import { useEffect, useRef, useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";

const DEFAULT_ALLOWED_TYPES = [
  "image/jpeg",
  "image/png",
  "image/jpg",
  "image/gif",
  "image/webp",
];

const loadImageFromUrl = (url) =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error("Could not load selected image."));
    image.src = url;
  });

const createCroppedBlob = async ({ imageUrl, crop, output, quality = 0.92 }) => {
  const image = await loadImageFromUrl(imageUrl);
  const imageWidth = image.naturalWidth;
  const imageHeight = image.naturalHeight;
  const targetAspect = output.width / output.height;

  let baseCropWidth = imageWidth;
  let baseCropHeight = imageWidth / targetAspect;

  if (baseCropHeight > imageHeight) {
    baseCropHeight = imageHeight;
    baseCropWidth = imageHeight * targetAspect;
  }

  const zoom = Math.max(1, Number(crop.zoom || 1));
  const cropWidth = baseCropWidth / zoom;
  const cropHeight = baseCropHeight / zoom;

  const minCenterX = cropWidth / 2;
  const maxCenterX = imageWidth - cropWidth / 2;
  const minCenterY = cropHeight / 2;
  const maxCenterY = imageHeight - cropHeight / 2;

  const requestedCenterX = (Number(crop.x || 50) / 100) * imageWidth;
  const requestedCenterY = (Number(crop.y || 50) / 100) * imageHeight;

  const centerX = Math.min(Math.max(requestedCenterX, minCenterX), maxCenterX);
  const centerY = Math.min(Math.max(requestedCenterY, minCenterY), maxCenterY);

  const sourceX = centerX - cropWidth / 2;
  const sourceY = centerY - cropHeight / 2;

  const canvas = document.createElement("canvas");
  canvas.width = output.width;
  canvas.height = output.height;

  const context = canvas.getContext("2d");
  if (!context) {
    throw new Error("Could not prepare image crop context.");
  }

  context.drawImage(
    image,
    sourceX,
    sourceY,
    cropWidth,
    cropHeight,
    0,
    0,
    output.width,
    output.height,
  );

  const blob = await new Promise((resolve, reject) => {
    canvas.toBlob(
      (generatedBlob) => {
        if (generatedBlob) {
          resolve(generatedBlob);
        } else {
          reject(new Error("Could not generate cropped image."));
        }
      },
      "image/jpeg",
      quality,
    );
  });

  return blob;
};

const validateImage = ({ file, maxSizeBytes, allowedTypes, label }) => {
  if (!file) return `${label} is required.`;

  if (!allowedTypes.includes(file.type)) {
    return `${label} must be jpeg, png, jpg, gif, or webp.`;
  }

  if (file.size > maxSizeBytes) {
    return `${label} must be within ${Math.round(maxSizeBytes / (1024 * 1024))}MB.`;
  }

  return null;
};

export default function ImageCropUploader({
  label,
  outputDimension,
  maxSizeBytes = 1024 * 1024,
  accept = ".jpeg,.jpg,.png,.gif,.webp",
  allowedTypes = DEFAULT_ALLOWED_TYPES,
  onCroppedFileChange,
  onError,
}) {
  const [sourceUrl, setSourceUrl] = useState("");
  const [previewUrl, setPreviewUrl] = useState("");
  const [crop, setCrop] = useState({ x: 50, y: 50, zoom: 1 });
  const [isGenerating, setIsGenerating] = useState(false);
  const sourceRef = useRef("");
  const previewRef = useRef("");

  useEffect(() => {
    sourceRef.current = sourceUrl;
  }, [sourceUrl]);

  useEffect(() => {
    previewRef.current = previewUrl;
  }, [previewUrl]);

  useEffect(() => {
    return () => {
      if (sourceRef.current) URL.revokeObjectURL(sourceRef.current);
      if (previewRef.current) URL.revokeObjectURL(previewRef.current);
    };
  }, []);

  const emitError = (message) => {
    if (onError) onError(message);
  };

  const generatePreviewAndFile = async (nextSourceUrl, nextCrop) => {
    setIsGenerating(true);
    try {
      const croppedBlob = await createCroppedBlob({
        imageUrl: nextSourceUrl,
        crop: nextCrop,
        output: outputDimension,
      });

      const croppedFile = new File([croppedBlob], `cropped-${Date.now()}.jpg`, {
        type: "image/jpeg",
      });

      if (croppedFile.size > maxSizeBytes) {
        emitError(`Cropped image is larger than ${Math.round(maxSizeBytes / (1024 * 1024))}MB.`);
        return;
      }

      if (previewRef.current) {
        URL.revokeObjectURL(previewRef.current);
      }

      const nextPreviewUrl = URL.createObjectURL(croppedBlob);
      setPreviewUrl(nextPreviewUrl);
      if (onCroppedFileChange) onCroppedFileChange(croppedFile);
    } catch (error) {
      emitError(error.message || "Failed to generate crop preview.");
    } finally {
      setIsGenerating(false);
    }
  };

  const handleSelect = async (event) => {
    const file = event.target.files?.[0] || null;
    if (!file) return;

    const imageError = validateImage({
      file,
      maxSizeBytes,
      allowedTypes,
      label: "Image",
    });

    if (imageError) {
      emitError(imageError);
      return;
    }

    if (sourceRef.current) {
      URL.revokeObjectURL(sourceRef.current);
    }

    const nextSourceUrl = URL.createObjectURL(file);
    setSourceUrl(nextSourceUrl);

    const resetCrop = { x: 50, y: 50, zoom: 1 };
    setCrop(resetCrop);
    await generatePreviewAndFile(nextSourceUrl, resetCrop);
  };

  const handleCropChange = (field, value) => {
    setCrop((prev) => ({ ...prev, [field]: value }));
  };

  const handleApplyCrop = async () => {
    if (!sourceUrl) {
      emitError("Please select an image first.");
      return;
    }
    await generatePreviewAndFile(sourceUrl, crop);
  };

  return (
    <div className="space-y-3 rounded-md border border-cblack6 p-3">
      <div className="space-y-2">
        <Label className="text-cwhite">{label}</Label>
        <Input
          type="file"
          accept={accept}
          className="text-cwhite file:text-cwhite file:font-medium"
          onChange={handleSelect}
        />
      </div>

      {sourceUrl ? (
        <>
          <div className="space-y-2">
            <p className="text-xs text-cwhite2">Selected image preview</p>
            <img
              src={sourceUrl}
              alt="Selected"
              className="w-full max-h-44 rounded object-contain"
            />
          </div>

          <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
            <div className="space-y-1">
              <Label className="text-xs text-cwhite">Crop X ({crop.x}%)</Label>
              <input
                type="range"
                min="0"
                max="100"
                step="1"
                value={crop.x}
                onChange={(event) => handleCropChange("x", Number(event.target.value))}
                className="w-full"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-cwhite">Crop Y ({crop.y}%)</Label>
              <input
                type="range"
                min="0"
                max="100"
                step="1"
                value={crop.y}
                onChange={(event) => handleCropChange("y", Number(event.target.value))}
                className="w-full"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-cwhite">Zoom ({crop.zoom.toFixed(1)}x)</Label>
              <input
                type="range"
                min="1"
                max="4"
                step="0.1"
                value={crop.zoom}
                onChange={(event) => handleCropChange("zoom", Number(event.target.value))}
                className="w-full"
              />
            </div>
          </div>

          <Button type="button" variant="outline" onClick={handleApplyCrop} isLoading={isGenerating}>
            Apply Crop
          </Button>

          {previewUrl ? (
            <div className="space-y-2">
              <p className="text-xs text-cwhite2">
                Cropped preview ({outputDimension.width} x {outputDimension.height})
              </p>
              <img
                src={previewUrl}
                alt="Cropped"
                className="w-full max-w-[546px] rounded border border-cblack6"
              />
            </div>
          ) : null}
        </>
      ) : null}
    </div>
  );
}
